/**
 * Pure helpers for the Personalization pane: the three-file persona model, the
 * display-name line in USER.md, and read-only markdown colouring.
 *
 * MEMORY.md is deliberately absent from every list here: it is the agent's own
 * memory, never shown or written.
 */

import { SIDECAR_CODES } from '../../sidecar/request';

export const PERSONALIZATION_FILES = [
  'IDENTITY.md',
  'SOUL.md',
  'USER.md',
] as const;

export type PersonalizationFileName = (typeof PERSONALIZATION_FILES)[number];

/** Tabs are cut by subject (the agent, you), not one per file. */
export const PERSONALIZATION_TABS = [
  { id: 'agent', label: 'Agent', files: ['IDENTITY.md', 'SOUL.md'] },
  { id: 'user', label: 'User', files: ['USER.md'] },
] as const satisfies readonly {
  readonly id: string;
  readonly label: string;
  readonly files: readonly PersonalizationFileName[];
}[];

export type PersonalizationTabId = (typeof PERSONALIZATION_TABS)[number]['id'];

export const PERSONALIZATION_FILE_LABELS: Readonly<
  Record<PersonalizationFileName, string>
> = {
  'IDENTITY.md': 'Identity',
  'SOUL.md': 'Persona',
  'USER.md': 'User Persona',
};

export const PERSONALIZATION_FILE_DESCRIPTIONS: Readonly<
  Record<PersonalizationFileName, string>
> = {
  'IDENTITY.md': 'who the agent is (name, role, mission)',
  'SOUL.md': 'how the agent talks (tone, opinions, brevity)',
  'USER.md': 'what it should know about you (markets, sizing, shorthand)',
};

// ── The gateway snapshot ────────────────────────────────────────────────────

export interface PersonalizationSnapshot {
  readonly files: Readonly<Record<PersonalizationFileName, string>>;
  /**
   * Files the agent could not read (over its cap, a symlink, not a regular
   * file). Their `files` entry is "" as a report, not as content: the pane
   * shows them read-only and never writes them.
   */
  readonly unreadable: readonly PersonalizationFileName[];
}

/**
 * Parse the body the sidecar answers `GET /personalization/v1/state` and
 * `POST /personalization/v1/file` with. `null` means whatever answered did so
 * with an unrelated shape, i.e. it has no such feature. Only the three known
 * names are read out of `files`.
 */
export function parsePersonalizationSnapshot(
  payload: unknown,
): PersonalizationSnapshot | null {
  if (payload == null || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;

  const rawFiles = p.files;
  if (rawFiles == null || typeof rawFiles !== 'object') return null;
  const fileMap = rawFiles as Record<string, unknown>;
  const files = {} as Record<PersonalizationFileName, string>;
  for (const name of PERSONALIZATION_FILES) {
    const value = fileMap[name];
    files[name] = typeof value === 'string' ? value : '';
  }

  const rawUnreadable = Array.isArray(p.unreadable) ? p.unreadable : [];
  const unreadable = PERSONALIZATION_FILES.filter((name) =>
    rawUnreadable.includes(name),
  );

  return { files, unreadable };
}

// ── What a failure says ─────────────────────────────────────────────────────
//
// The sidecar answers a refusal with `{"error": "<code>"}` and a status; the
// pane shows a sentence, never the code. This table is the one place the
// wording lives. Keys are upper-cased codes: the sidecar's own
// (`file_unreadable`, matched case-insensitively) and the ones the request
// primitive mints for failures that never reached it (`SIDECAR_CODES`).

export const PERSONALIZATION_SENTENCES: Readonly<Record<string, string>> = {
  // The sidecar's refusals.
  UNKNOWN_FILE: "That isn't one of the three personalization files.",
  FILE_TOO_LARGE: "That's too long for one file. Trim it and save again.",
  PAYLOAD_TOO_LARGE: "That's too large to save.",
  RATE_LIMITED: 'Too many changes at once — wait a moment and try again.',
  FILE_UNREADABLE:
    "The agent couldn't read that file, so it can't be saved over from here.",
  WRITE_FAILED: "Couldn't write that to the agent. Try again in a moment.",
  READ_FAILED:
    "Couldn't read your personalization from the agent. Try again in a moment.",
  // Failures on the way there.
  NETWORK: "The agent isn't reachable right now. Try again in a moment.",
  NO_CREDENTIAL:
    "This terminal isn't signed in to the agent yet. Try again in a moment.",
  BAD_RESPONSE: "The agent sent something the terminal couldn't read.",
};

/** The pane's verdict when nothing in front of it implements the feature. */
export const PERSONALIZATION_UNAVAILABLE_SENTENCE =
  "Personalization isn't available on this gateway.";

/**
 * The sentence for a failed sidecar call: the code's own when the table has
 * it, else one by status. `op` only shapes the fallback — a read that failed
 * and a change that was rejected are different news.
 */
export function personalizationFailureText(
  failure: { readonly status: number; readonly code: string | null },
  op: 'read' | 'write',
): string {
  const known = PERSONALIZATION_SENTENCES[failure.code?.toUpperCase() ?? ''];
  if (known !== undefined) return known;
  if (failure.status === 404 || failure.code === SIDECAR_CODES.NO_SIDECAR) {
    return PERSONALIZATION_UNAVAILABLE_SENTENCE;
  }
  if (failure.status === 0) return PERSONALIZATION_SENTENCES.NETWORK!;
  if (failure.status >= 500) {
    return "The agent couldn't complete that — try again in a moment.";
  }
  return op === 'read'
    ? `The agent couldn't load your personalization (${failure.status}).`
    : `The agent rejected that change (${failure.status}).`;
}

// ── The display-name line in USER.md ────────────────────────────────────────

/** Line 1 of USER.md, by contract: `- Call me <name>.` */
const NAME_LINE = /^-\s*Call me (.*?)\.?\s*$/;

/**
 * The lazy capture gives only the final `.` to the optional terminator, so
 * `- Call me Dr. X.` reads `Dr. X` and a name ending in a period round-trips
 * (`Bob.` → `- Call me Bob..` → `Bob.`).
 */
export function parseDisplayName(userMd: string): string {
  const firstLine = splitLines(userMd)[0] ?? '';
  const match = NAME_LINE.exec(firstLine);
  return match?.[1]?.trim() ?? '';
}

function splitLines(text: string): string[] {
  return text.split(/\r?\n/);
}

/** Rewrite (or insert) line 1 only; every other byte of the file is kept. */
export function withDisplayName(userMd: string, name: string): string {
  const line = `- Call me ${name}.`;
  if (userMd.length === 0) return `${line}\n`;
  const firstEol = /\r?\n/.exec(userMd);
  const firstLine =
    firstEol === null ? userMd : userMd.slice(0, firstEol.index);
  if (NAME_LINE.test(firstLine)) {
    return line + (firstEol === null ? '' : userMd.slice(firstEol.index));
  }
  return line + (firstEol?.[0] ?? '\n') + userMd;
}

/** Mirrors the sidecar's per-file cap; refused here rather than by a 413, or by
 *  the socket's message cap killing the session on ⌘S. */
export const MAX_PERSONALIZATION_FILE_BYTES = 64 * 1024;

export function personalizationByteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

/**
 * What a textarea holds. The browser normalises a textarea's value to LF, so
 * every comparison between a draft and a stored file, and every row the
 * highlight layer draws under the caret, has to be made on the LF form.
 */
export function toLF(text: string): string {
  return text.replace(/\r\n/g, '\n');
}

/**
 * Restore `stored`'s line endings on `draft`, which a textarea has normalised
 * to LF.
 *
 * A wholly-CRLF file is uniform, so every ending comes back. On a MIXED file
 * endings are restored per line: a line that is still at its original index
 * keeps the ending it had, and anything past the end of the original takes
 * the file's dominant ending. Editing line 3 of a mixed file leaves lines 1,
 * 2 and 4 byte-identical.
 *
 * The index is the whole rule, and its limit: INSERTING or DELETING a line
 * shifts every ending below it by one — on a file whose endings alternate,
 * deleting line 1 moves all of them. Endings only, never content; restoring
 * them exactly would need a diff, which is not worth it for a file the user
 * is editing as plain text.
 */
export function matchLineEndings(stored: string, draft: string): string {
  // Normalise first, unconditionally: a CRLF-carrying draft over an LF-only
  // `stored` must not flip that whole file to CRLF.
  const normalised = toLF(draft);
  const storedEndings = stored.match(/\r?\n/g) ?? [];
  const crlf = storedEndings.filter((e) => e === '\r\n').length;
  if (crlf === 0) return normalised;
  const dominant = crlf * 2 >= storedEndings.length ? '\r\n' : '\n';

  const lines = normalised.split('\n');
  // split() leaves a trailing "" for a file that ends in a newline; that entry
  // is not a line and must not be given an ending of its own.
  let out = '';
  for (let i = 0; i < lines.length; i += 1) {
    out += lines[i];
    if (i < lines.length - 1) out += storedEndings[i] ?? dominant;
  }
  return out;
}

// ── Read-only rendering ─────────────────────────────────────────────────────

export type TokenColor = 'plain' | 'dim' | 'heading' | 'string';

export interface Token {
  readonly text: string;
  readonly color: TokenColor;
}

export interface EditorLine {
  readonly gutter: string;
  readonly tokens: readonly Token[];
}

function token(text: string, color: TokenColor): Token {
  return { text, color };
}

/** Double-quoted spans in string colour, the rest in `base`. */
function inlineTokens(text: string, base: TokenColor = 'plain'): Token[] {
  const tokens: Token[] = [];
  const re = /"[^"]*"/g;
  let last = 0;
  for (let m = re.exec(text); m != null; m = re.exec(text)) {
    if (m.index > last) tokens.push(token(text.slice(last, m.index), base));
    tokens.push(token(m[0], 'string'));
    last = m.index + m[0].length;
  }
  if (last < text.length) tokens.push(token(text.slice(last), base));
  return tokens;
}

export function tokenizeMarkdownLine(line: string): readonly Token[] {
  const heading = /^(#{1,6})(\s+)(.*)$/.exec(line);
  if (heading != null) {
    return [
      token(heading[1]!, 'dim'),
      token(heading[2]!, 'plain'),
      token(heading[3]!, 'heading'),
    ];
  }
  const bullet = /^(\s*[-*]\s+)(.*)$/.exec(line);
  if (bullet != null) {
    return [token(bullet[1]!, 'dim'), ...inlineTokens(bullet[2]!)];
  }
  return inlineTokens(line);
}

/** Read view: a trailing newline does not produce a phantom numbered line. */
export function markdownLines(text: string): readonly EditorLine[] {
  const lf = toLF(text);
  const raw = lf.length === 0 ? [] : lf.split('\n');
  if (raw.length > 0 && raw[raw.length - 1] === '') raw.pop();
  return raw.map((line, i) => ({
    gutter: String(i + 1),
    tokens: tokenizeMarkdownLine(line),
  }));
}

/**
 * Edit view: exactly one row per textarea line, trailing empty line included,
 * so the highlight layer and the textarea cannot drift. Rows are cut on the
 * LF form: a CRLF-stored file would otherwise leave a `\r` on every row that
 * the textarea's value does not have, and wrap differently from the caret.
 */
export function editorLines(text: string): readonly EditorLine[] {
  return toLF(text)
    .split('\n')
    .map((line, i) => ({
      gutter: String(i + 1),
      tokens: tokenizeMarkdownLine(line),
    }));
}
